import { useEffect, useState } from "react";

const POLL_MS = 30_000;

/**
 * useRadarSiteHealth — poll /radar/sites/health every 30 s.
 *
 * Returns:
 *   siteHealthMap     — legacy { [siteId]: "online"|"stale"|"offline" }
 *   siteHealthDetails — enriched { [siteId]: SiteHealthDetail object }
 *
 * On network error the last known state is preserved (no reset).
 *
 * @param {string} apiBase  API root URL.
 * @returns {{ siteHealthMap: Object, siteHealthDetails: Object }}
 */
export default function useRadarSiteHealth(apiBase) {
  const [siteHealthMap, setSiteHealthMap] = useState({});
  const [siteHealthDetails, setSiteHealthDetails] = useState({});

  useEffect(() => {
    let cancelled = false;
    const fetchHealth = async () => {
      try {
        const res = await fetch(`${apiBase}/radar/sites/health`, { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const nextMap = {};
        const nextDetails = {};
        for (const [site, info] of Object.entries(data.sites ?? {})) {
          nextMap[site] = info.health ?? "unknown";
          nextDetails[site] = info;
        }
        setSiteHealthMap(nextMap);
        setSiteHealthDetails(nextDetails);
      } catch (_) {
        // keep last known health on network error
      }
    };
    fetchHealth();
    const id = setInterval(fetchHealth, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [apiBase]);

  return { siteHealthMap, siteHealthDetails };
}
